(() => {
  if (window.OBBlackFridayQuote) return;

  const ENDPOINT = 'https://v1v84d-35.myshopify.com/api/2026-07/graphql.json';
  const COUNTRIES = 'AT,BE,CZ,DE,DK,EE,FI,FR,GR,HU,IT,LT,LU,LV,NL,PL,PT,RO,SE,SI'.split(',');
  const CURRENCIES = ['EUR', 'CZK', 'HUF', 'PLN', 'RON', 'SEK'];
  const MARKER = '_ob_bf_quote_line';
  const FRAGMENT = `fragment OBQuoteCart on Cart {
    id totalQuantity buyerIdentity { countryCode }
    cost { totalAmount { amount currencyCode } }
    discountCodes { code applicable }
    discountAllocations { discountedAmount { amount currencyCode } ... on CartAutomaticDiscountAllocation { title } }
    lines(first:100) { pageInfo { hasNextPage } nodes {
      id quantity attributes { key value }
      merchandise { ... on ProductVariant { id } }
      cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
      discountAllocations { discountedAmount { amount currencyCode } ... on CartAutomaticDiscountAllocation { title } }
      ... on CartLine { parentRelationship { parent { id } } }
    } }
  }`;
  const QUERIES = {
    create: `mutation OBQuoteCreate($input:CartInput!,$country:CountryCode!) @inContext(country:$country) { cartCreate(input:$input) { cart { ...OBQuoteCart } userErrors { code } } } ${FRAGMENT}`,
    add: `mutation OBQuoteAdd($cartId:ID!,$lines:[CartLineInput!]!,$country:CountryCode!) @inContext(country:$country) { cartLinesAdd(cartId:$cartId,lines:$lines) { cart { ...OBQuoteCart } userErrors { code } } } ${FRAGMENT}`,
    remove: `mutation OBQuoteRemove($cartId:ID!,$lineIds:[ID!]!,$country:CountryCode!) @inContext(country:$country) { cartLinesRemove(cartId:$cartId,lineIds:$lineIds) { cart { ...OBQuoteCart } userErrors { code } } } ${FRAGMENT}`,
    read: `query OBQuoteRead($cartId:ID!,$country:CountryCode!) @inContext(country:$country) { cart(id:$cartId) { ...OBQuoteCart } } ${FRAGMENT}`
  };
  const FIELDS = { create: 'cartCreate', add: 'cartLinesAdd', remove: 'cartLinesRemove' };
  const fail = () => { throw new Error('Native quote unavailable'); };
  const aborted = () => new DOMException('Quote cancelled', 'AbortError');
  const gid = (value, kind = 'ProductVariant') => {
    const id = String(value);
    if (!/^\d+$/.test(id)) fail();
    return 'gid://shopify/' + kind + '/' + id;
  };
  const integer = value => Number.isSafeInteger(value) && value >= 0;
  const minor = (money, currency) => {
    if (money?.currencyCode !== currency || !/^\d+(?:\.\d+)?$/.test(String(money?.amount))) fail();
    const [whole, fraction = ''] = String(money.amount).split('.');
    if (/[1-9]/.test(fraction.slice(2))) fail();
    const amount = Number(whole) * 100 + Number((fraction + '00').slice(0, 2));
    if (!integer(amount)) fail();
    return amount;
  };
  const attributes = object => {
    const result = Object.entries(object || {}).filter(([key, value]) => key !== MARKER && value != null)
      .map(([key, value]) => {
        if (typeof value === 'object') fail();
        return { key, value: String(value) };
      }).sort((a, b) => a.key.localeCompare(b.key));
    if (result.length > 249) fail();
    return result;
  };
  const campaignAmount = (cart, titles, currency) => {
    const allocations = [...(cart.discountAllocations || []), ...cart.lines.nodes.flatMap(line => line.discountAllocations || [])];
    return allocations.reduce((sum, allocation) => {
      const amount = minor(allocation.discountedAmount, currency);
      return sum + (titles.includes(allocation.title) ? amount : 0);
    }, 0);
  };
  const ajaxCampaignAmount = (cart, titles) => {
    const allocations = (cart.items || []).flatMap(line => line.line_level_discount_allocations || []);
    let result = allocations.reduce((sum, entry) => sum + (titles.includes(entry.discount_application?.title) ? Number(entry.amount) : 0), 0);
    for (const entry of cart.cart_level_discount_applications || []) {
      if (titles.includes(entry.title)) result += Number(entry.total_allocated_amount);
    }
    if (!integer(result)) fail();
    return result;
  };
  const codesFrom = cart => {
    const codes = (cart.discount_codes || []).map(entry => typeof entry === 'string' ? entry : entry.code);
    const active = (cart.discount_codes || []).filter(entry => entry && typeof entry === 'object' && entry.applicable === true).map(entry => entry.code);
    const apps = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(entry => entry.discount_application))];
    for (const app of apps) if (app?.type === 'discount_code') { codes.push(app.code || app.title); active.push(app.code || app.title); }
    const unique = values => [...new Set(values.filter(code => typeof code === 'string' && code.trim()).map(code => code.trim().toUpperCase()))].sort();
    return { codes: unique(codes), active: unique(active) };
  };
  const normalize = request => {
    if (!COUNTRIES.includes(request.country) || !CURRENCIES.includes(request.currency) || request.cart?.currency !== request.currency) fail();
    if (!Array.isArray(request.cart.items) || !Array.isArray(request.items) || !request.items.length || request.cart.items.length + request.items.length > 80) fail();
    if (!Array.isArray(request.discountTitles) || !request.discountTitles.length || request.discountTitles.some(title => typeof title !== 'string')) fail();
    const existing = request.cart.items.map((item, index) => {
      if (item.item_components?.length || item.components?.length || !integer(item.quantity) || item.quantity < 1) fail();
      const unitPrice = item.original_price ?? item.price;
      if (!integer(unitPrice) || !integer(item.original_line_price) || unitPrice * item.quantity !== item.original_line_price) fail();
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.id;
      return {
        id: String(item.variant_id || item.id), quantity: item.quantity, price: unitPrice,
        key: String(item.key || index), parentKey: item.parent_relationship?.parent_key || null,
        properties: attributes(item.properties), sellingPlan: sellingPlan ? gid(sellingPlan, 'SellingPlan') : null,
        marker: 'existing-' + index
      };
    });
    const items = request.items.map((item, index) => {
      gid(item.id);
      if (!integer(item.quantity) || item.quantity < 1 || !integer(item.price) || item.price <= 0) fail();
      if (index === 0 ? Boolean(item.parent_id) : String(item.parent_id) !== String(request.items[0].id)) fail();
      return { id: String(item.id), quantity: item.quantity, price: item.price, marker: 'selected-' + index };
    });
    if (new Set(existing.map(item => item.key)).size !== existing.length) fail();
    for (const item of existing) {
      gid(item.id);
      if (item.parentKey && !existing.some(parent => parent.key === item.parentKey && !parent.parentKey)) fail();
    }
    const codes = codesFrom(request.cart);
    const discountCodes = codes.codes;
    const activeCodes = codes.active;
    const cartAttributes = attributes(request.cart.attributes);
    const beforeAjax = ajaxCampaignAmount(request.cart, request.discountTitles);
    const baseline = { existing, discountCodes, activeCodes, cartAttributes, country: request.country, currency: request.currency, beforeAjax };
    const cartFingerprint = JSON.stringify(baseline);
    const signature = JSON.stringify([cartFingerprint, items, request.discountTitles]);
    return { ...baseline, items, titles: [...request.discountTitles], cartFingerprint, signature };
  };

  class NativeQuoteService {
    constructor() {
      this.queue = Promise.resolve();
      this.inflight = new Map();
      this.cache = new Map();
      this.closed = false;
      this.lifecycle = new AbortController();
      this.state = null;
    }

    async request(operation, variables) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(ENDPOINT, {
          method: 'POST', credentials: 'omit', cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ query: QUERIES[operation], variables }), signal: controller.signal
        });
        if (!response.ok) fail();
        const result = await response.json();
        if (result.errors?.length) fail();
        const payload = operation === 'read' ? { cart: result.data?.cart } : result.data?.[FIELDS[operation]];
        if (!payload?.cart || payload.userErrors?.length) fail();
        const cart = payload.cart;
        if (!cart.id || !Array.isArray(cart.lines?.nodes) || cart.lines.pageInfo?.hasNextPage) fail();
        if (cart.buyerIdentity?.countryCode !== variables.country) fail();
        return cart;
      } finally {
        clearTimeout(timer);
      }
    }

    async mutate(operation, variables) {
      try {
        const cart = await this.request(operation, variables);
        if (this.state) { this.state.cart = cart; this.state.dirty = false; }
        return cart;
      } catch (error) {
        if (this.state) this.state.dirty = true;
        throw error;
      }
    }

    lineInput(item, parentLineId) {
      const input = {
        merchandiseId: gid(item.id), quantity: item.quantity,
        attributes: [...(item.properties || []), { key: MARKER, value: item.marker }]
      };
      if (item.sellingPlan) input.sellingPlanId = item.sellingPlan;
      if (parentLineId) input.parent = { lineId: parentLineId };
      return input;
    }

    byMarker(cart, marker) {
      const matches = cart.lines.nodes.filter(line => line.attributes.some(attribute => attribute.key === MARKER && attribute.value === marker));
      if (matches.length !== 1) fail();
      return matches[0];
    }

    validateLines(cart, expected, currency) {
      minor(cart.cost?.totalAmount, currency);
      if (cart.lines.nodes.length !== expected.length) fail();
      for (const item of expected) {
        const line = this.byMarker(cart, item.marker);
        if (line.merchandise?.id !== gid(item.id) || line.quantity !== item.quantity) fail();
        if (minor(line.cost.subtotalAmount, currency) !== item.price * item.quantity) fail();
        minor(line.cost.totalAmount, currency);
      }
    }

    async emptyState() {
      if (!this.state) return;
      if (this.state.dirty) {
        this.state.cart = await this.request('read', { cartId: this.state.cart.id, country: this.state.country });
        this.state.dirty = false;
      }
      const roots = this.state.cart.lines.nodes.filter(line => !line.parentRelationship?.parent?.id).map(line => line.id);
      if (roots.length) {
        const cart = await this.mutate('remove', { cartId: this.state.cart.id, lineIds: roots, country: this.state.country });
        if (cart.totalQuantity !== 0 || cart.lines.nodes.length) fail();
      }
      this.state.cleared = true;
    }

    async establishBaseline(context) {
      if (this.state && (this.state.fingerprint !== context.cartFingerprint || this.state.dirty)) {
        await this.emptyState();
        this.state = null;
      }
      if (!this.state) {
        const roots = context.existing.filter(item => !item.parentKey);
        const cart = await this.request('create', {
          country: context.country,
          input: { buyerIdentity: { countryCode: context.country }, discountCodes: context.discountCodes, attributes: context.cartAttributes, lines: roots.map(item => this.lineInput(item)) }
        });
        this.state = { cart, country: context.country, fingerprint: context.cartFingerprint, dirty: false, cleared: false };
        const children = context.existing.filter(item => item.parentKey);
        if (children.length) {
          const lines = children.map(item => {
            const parent = roots.find(candidate => candidate.key === item.parentKey);
            return this.lineInput(item, this.byMarker(cart, parent.marker).id);
          });
          await this.mutate('add', { cartId: cart.id, lines, country: context.country });
        }
      } else if (this.state.cleared) {
        const roots = context.existing.filter(item => !item.parentKey);
        if (roots.length) await this.mutate('add', {
          cartId: this.state.cart.id, country: context.country, lines: roots.map(item => this.lineInput(item))
        });
        const children = context.existing.filter(item => item.parentKey);
        if (children.length) {
          const lines = children.map(item => {
            const parent = roots.find(candidate => candidate.key === item.parentKey);
            return this.lineInput(item, this.byMarker(this.state.cart, parent.marker).id);
          });
          await this.mutate('add', { cartId: this.state.cart.id, lines, country: context.country });
        }
        this.state.cleared = false;
      } else {
        const selectedRoots = this.state.cart.lines.nodes.filter(line =>
          !line.parentRelationship?.parent?.id && line.attributes.some(attribute => attribute.key === MARKER && attribute.value.startsWith('selected-'))
        ).map(line => line.id);
        if (selectedRoots.length) await this.mutate('remove', { cartId: this.state.cart.id, lineIds: selectedRoots, country: context.country });
        else this.state.cart = await this.request('read', { cartId: this.state.cart.id, country: context.country });
      }
      const baseline = this.state.cart;
      this.validateLines(baseline, context.existing, context.currency);
      if (context.activeCodes.some(code => !(baseline.discountCodes || []).some(entry => entry.code.toUpperCase() === code && entry.applicable))) fail();
      const amount = campaignAmount(baseline, context.titles, context.currency);
      if (amount !== context.beforeAjax) fail();
      return amount;
    }

    async perform(context) {
      try {
      const campaignBefore = await this.establishBaseline(context);
      if (this.closed) throw aborted();
      const base = context.items[0];
      let cart = await this.mutate('add', { cartId: this.state.cart.id, country: context.country, lines: [this.lineInput(base)] });
      const parent = this.byMarker(cart, base.marker);
      if (context.items.length > 1) {
        cart = await this.mutate('add', {
          cartId: cart.id, country: context.country,
          lines: context.items.slice(1).map(item => this.lineInput(item, parent.id))
        });
      }
      this.validateLines(cart, [...context.existing, ...context.items], context.currency);
      for (const item of context.items.slice(1)) if (this.byMarker(cart, item.marker).parentRelationship?.parent?.id !== parent.id) fail();
      const campaignAfter = campaignAmount(cart, context.titles, context.currency);
      const subtotal = context.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const discount = Math.max(0, campaignAfter - campaignBefore);
      if (!integer(subtotal) || !integer(discount) || discount > subtotal) fail();
      const selectedLineTotal = context.items.reduce((sum, item) => sum + minor(this.byMarker(cart, item.marker).cost.totalAmount, context.currency), 0);
      return Object.freeze({ subtotal, discount, total: subtotal - discount, currency: context.currency,
        selectedLineTotal, campaignBefore, campaignAfter, signature: context.signature, cartFingerprint: context.cartFingerprint });
      } finally {
        await this.emptyState();
      }
    }

    subscribe(promise, signal, entry) {
      const lifecycle = this.lifecycle.signal;
      if (signal?.aborted || lifecycle.aborted) return Promise.reject(aborted());
      if (entry) entry.consumers++;
      return new Promise((resolve, reject) => {
        let settled = false;
        const cancel = () => { cleanup(); reject(aborted()); };
        const cleanup = () => {
          if (settled) return;
          settled = true;
          if (entry) entry.consumers--;
          signal?.removeEventListener('abort', cancel);
          lifecycle.removeEventListener('abort', cancel);
        };
        signal?.addEventListener('abort', cancel, { once: true });
        lifecycle.addEventListener('abort', cancel, { once: true });
        promise.then(value => { cleanup(); resolve(value); }, error => { cleanup(); reject(error); });
      });
    }

    quote(request) {
      if (this.closed || request.signal?.aborted) return Promise.reject(aborted());
      let context;
      try { context = normalize(request); } catch (error) { return Promise.reject(error); }
      const cached = this.cache.get(context.signature);
      if (!request.force && cached && Date.now() - cached.time < 30000) return this.subscribe(Promise.resolve(cached.value), request.signal);
      const key = context.signature + (request.force ? ':force' : '');
      if (!this.inflight.has(key)) {
        const entry = { consumers: 0, promise: null };
        const task = this.queue.then(async () => {
          if (this.closed || entry.consumers === 0) throw aborted();
          const value = await this.perform(context);
          this.cache.set(context.signature, { value, time: Date.now() });
          if (this.cache.size > 8) this.cache.delete(this.cache.keys().next().value);
          return value;
        });
        this.queue = task.catch(() => {});
        entry.promise = task;
        this.inflight.set(key, entry);
        task.then(() => this.inflight.delete(key), () => this.inflight.delete(key));
      }
      const entry = this.inflight.get(key);
      return this.subscribe(entry.promise, request.signal, entry);
    }

    dispose() {
      if (this.closed) return this.queue;
      this.closed = true;
      this.lifecycle.abort();
      this.cache.clear();
      this.queue = this.queue.then(() => this.emptyState()).catch(() => {}).finally(() => { this.state = null; });
      return this.queue;
    }
  }

  window.OBBlackFridayQuote = Object.freeze({ create: () => new NativeQuoteService() });
})();

